import { AfterViewChecked, ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { Guid } from '../utils';
import { DataFieldComponentBase } from './datafield-base-component';
import { DataFieldGroup, DataFieldTypes, DataFieldValue } from './models';
import { DataField } from './models/datafield';
import { DataFieldTextBoxComponent } from './datafield-textbox.component';
import { DataFieldCheckBoxComponent } from './datafield-checkbox.component';
import { DataFieldDropDownComponent } from './datafield-dropdown.component';
import { DataFieldPasswordComponent } from './datafield-password.component';
import { DataFieldFileUploadComponent } from './datafield-file-upload.component';
import { DataFieldArtifactXmlPickerComponent } from './datafield-artifact-xml-picker.component';
import { DataFieldArtifactAssemblyPickerComponent } from './datafield-artifact-assembly-picker.component';
import { DatafieldXpathComponent } from './datafield-xpath.component';

@Component({
    selector: 'ep-datafield-array',
    templateUrl: './datafield-array.component.html',
    styleUrls: ['./datafield-array.component.scss'],
    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
    inputs: ['isAlwaysEditable', 'datafield', 'formValidationGroup'],
    // eslint-disable-next-line @angular-eslint/no-outputs-metadata-property
    outputs: ['valueChanged'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DataFieldTextBoxComponent, DataFieldCheckBoxComponent, DataFieldDropDownComponent, DataFieldPasswordComponent, DataFieldFileUploadComponent, DataFieldArtifactXmlPickerComponent, DataFieldArtifactAssemblyPickerComponent, DatafieldXpathComponent]
})
export class DataFieldArrayComponent extends DataFieldComponentBase implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

    @ViewChildren('datafieldComponent')
    public allDataFieldComponents: QueryList<DataFieldComponentBase>;

    private validationControlNames: string[] = [];
    datafieldGroups: DataFieldGroup[] = [];

    constructor(private ref: ChangeDetectorRef) {
        super();
    }

    override ngOnInit() {
        if (this.datafield.Value) {
            this.datafield.Value.forEach((value, index) => {

                let arrayFields: DataField[] = [];
                this.datafield.Array.ArrayFields.forEach((field) => {
                    const af = Object.assign({}, field);
                    af.Value = value[af.CallbackReferenceId];
                    af.UniqueId = Guid.newGuid();
                    arrayFields.push(af);
                });
                arrayFields = arrayFields.sort((df1: DataField, df2: DataField) => df1.SortOrder - df2.SortOrder);

                const group = new DataFieldGroup();
                group.DataFields = arrayFields;
                this.datafieldGroups.push(group);
            });
        }

        this.setValidators();
        this.checkDependsOnFieldsInitial();
    }

    ngOnDestroy(): void {
        this.clearValidators(); // Make sure to remove the dynamic controls from the form-group if this component is disposed.
    }

    ngAfterViewChecked(): void {
        this.updateValueAndValidity();
        this.ref.detectChanges();
    }

    onValueChanged(event: any) {
        this.datafield.Value = this.generateValueFromModel();
        this.updateValueAndValidity();
        this.checkDependsOnFields(event);
    }

    private setValidators() {
        const validationGroup = {};
        if (this.datafieldGroups) {
            this.datafieldGroups.forEach(group => {
                group.DataFields.forEach(datafield => {
                    const validationIdentifier = DataFieldComponentBase.generateIdentifier(datafield);
                    this.validationControlNames.push(validationIdentifier);

                    if (datafield.IsHidden || datafield.DataType.DataTypeEnum == DataFieldTypes.CheckBox || datafield.DataType.DataTypeEnum == DataFieldTypes.File) {
                        validationGroup[validationIdentifier] = new UntypedFormControl(datafield.Value?.toString() ?? '');
                    }
                    else if (datafield.DataType.DataTypeEnum == DataFieldTypes.Password) {
                        DataField.RemapSecret(datafield);

                        const pwdValidator = (control: UntypedFormControl) => DataFieldArrayComponent.validatePassword(datafield, control);
                        validationGroup[validationIdentifier] = datafield.IsRequired ? new UntypedFormControl(datafield.Value || '', pwdValidator) : new UntypedFormControl(datafield.Value || '');
                    }
                    else if (datafield.DataType.DataTypeEnum == DataFieldTypes.AssemblyArtifact || datafield.DataType.DataTypeEnum == DataFieldTypes.XmlArtifact) {
                        const artifactValidator = (control: UntypedFormControl) => DataFieldArrayComponent.validateArtifact(datafield, control);
                        validationGroup[validationIdentifier] = datafield.IsRequired ? new UntypedFormControl(datafield.Value || '', artifactValidator) : new UntypedFormControl(datafield.Value || '');
                    }
                    else {
                        const validators = [];
                        // Skipping required validation for array data fields since this is handled by Min/Max properties on the array definition
                        if (datafield.IsRequired && datafield.DataType.DataTypeEnum !== DataFieldTypes.Array) validators.push(Validators.required);
                        if (datafield.RegEx) validators.push(Validators.pattern(datafield.RegEx));
                        validationGroup[validationIdentifier] = new UntypedFormControl(datafield.Value?.toString() ?? '', validators);
                    }

                    if (!!this.formValidationGroup.get(validationIdentifier)) {
                        //this.formValidationGroup.setControl(validationIdentifier, validationGroup[validationIdentifier])
                    }
                    else {
                        this.formValidationGroup.addControl(validationIdentifier, validationGroup[validationIdentifier]);
                    }
                });
            });
        }

        this.updateValueAndValidity();
    }

    checkDependsOnFieldsInitial() {
        if (this.datafieldGroups) {
            this.datafieldGroups.forEach(group => {
                group.DataFields.forEach(datafield => {
                    this.checkDependsOnFields(datafield);
                });
            });
        }
    }

    checkDependsOnFields(changedDatafield: DataField) {
        // Only check depends-on if it's a checkbox or dropdown...
        if (this.datafieldGroups && (changedDatafield.DataType.DataTypeEnum == DataFieldTypes.CheckBox || changedDatafield.DataType.DataTypeEnum == DataFieldTypes.DropDown)) {
            this.datafieldGroups.forEach(group => {

                if (group.DataFields.some(df => df.UniqueId == changedDatafield.UniqueId)) {
                    group.DataFields.forEach(datafield => {
                        if (datafield.DependsOnCallbackReferenceId == changedDatafield.CallbackReferenceId && datafield.DependsOnValues) {
                            const isHidden = changedDatafield.IsHidden || !datafield.DependsOnValues.some(v => changedDatafield.Value?.toString().toLocaleLowerCase() == v?.toLocaleLowerCase());
                            const isChanged = isHidden !== datafield.IsHidden;
                            datafield.IsHidden = isHidden;

                            if (datafield.IsHidden) {
                                this.formValidationGroup.controls[DataFieldComponentBase.generateIdentifier(datafield)].disable();
                            }
                            else {
                                this.formValidationGroup.controls[DataFieldComponentBase.generateIdentifier(datafield)].enable();
                            }

                            if (isChanged) {
                                // Recursive call - if current data-field has been hidden (or un-hidden), then fields depending on it will also need to be hidden (or un-hidden)
                                this.checkDependsOnFields(datafield);
                            }
                        }
                    });
                }
            });
        }

        this.updateValueAndValidity();
        this.ref.detectChanges();
    }

    private clearValidators() {
        if (this.formValidationGroup) { // Because unique identifiers are created for each control, we need to remove the validation if it has already been set. Otherwise the "old" validators will remain in the form and have effect on the valid/invalid state.
            this.validationControlNames.forEach(v => this.formValidationGroup.removeControl(v));
        }
    }

    private updateValueAndValidity() {
        if (this.formValidationGroup && this.formValidationGroup.enabled) {
            this.formValidationGroup.updateValueAndValidity();
        }
    }

    generateValueFromModel(): any[] {
        const values: any[] = [];
        this.datafieldGroups.forEach((group) => {
            const arrayValue: { [k: string]: any } = {};
            group.DataFields.forEach((field) => {
                if (field.DataType.DataTypeEnum == DataFieldTypes.Password) {
                    const passwordObj = new DataFieldValue();
                    passwordObj.Value = field.SecretId;
                    passwordObj.Secret = field.Value;
                    passwordObj.CallbackReferenceId = field.CallbackReferenceId;
                    arrayValue[field.CallbackReferenceId] = JSON.stringify(passwordObj);
                }
                else {
                    arrayValue[field.CallbackReferenceId] = field.Value;
                }
            });
            values.push(arrayValue);
        });

        return values;
    }

    addItem() {
        const arrayFields: DataField[] = [];

        const sortedFields = this.datafield.Array.ArrayFields.sort((df1: DataField, df2: DataField) => df1.SortOrder - df2.SortOrder);
        sortedFields.forEach((field) => {
            const af = Object.assign({}, field);
            af.Value = field.DefaultValue;
            af.UniqueId = Guid.newGuid();
            arrayFields.push(af);
        });

        const newGroup = new DataFieldGroup();
        newGroup.DataFields = arrayFields;
        this.datafieldGroups.push(newGroup);

        this.setValidators();

        newGroup.DataFields.forEach((field) => {
            this.checkDependsOnFields(field);
        });

        if (this.isEditable) {
            this.allDataFieldComponents
                .filter(e => !e.isEditable)
                .forEach(e => e.edit());
        }
    }

    removeItem(index: number) {
        if (index > -1) {

            this.datafieldGroups[index].DataFields.forEach((field) => {
                const identifier = DataFieldComponentBase.generateIdentifier(field);
                this.formValidationGroup.removeControl(identifier);
            });

            this.datafieldGroups.splice(index, 1);
            this.datafield.Value = this.generateValueFromModel();
            this.setValidators();
        }
    }

    allowAddItem(): boolean {
        return (this.isAlwaysEditable || this.isEditable) && (this.datafield.Array.MaxElements == 0 || (this.datafieldGroups?.length ?? 0) < this.datafield.Array.MaxElements);
    }

    allowRemoveItem(): boolean {
        return (this.isAlwaysEditable || this.isEditable);
    }

    get isValid() {
        if (this.formValidationGroup.disabled) {
            return true;
        }

        const arrayValid = (this.datafield.Array.MinElements > 0 && this.datafieldGroups.length >= this.datafield.Array.MinElements) || this.datafield.Array.MinElements == 0;

        if (!arrayValid) {
            this.formValidationGroup.controls[DataFieldComponentBase.generateIdentifier(this.datafield)].setErrors({ invalidNumberOfItems: true });
        }

        return arrayValid;
    }

    get errorText() {

        if (this.datafield.Array.MinElements > 0 && this.datafieldGroups.length < this.datafield.Array.MinElements) {
            const minVal = this.datafield.Array.MinElements;
            return `A minimum of ${minVal} item${minVal > 1 ? 's are' : ' is'} required`;
        }

        return 'Unknown error';
    }

    private static validateArtifact(datafield: DataField, control: UntypedFormControl): ValidationErrors | null {
        if (!datafield.Value || datafield.Value == Guid.empty) {
            return {
                required: {
                    valid: false
                }
            };
        }

        return null;
    }

    private static validatePassword(datafield: DataField, control: UntypedFormControl): ValidationErrors | null {
        return ((control && control.value) || (datafield && datafield.SecretId)) ? null : {
            required: {
                valid: false
            }
        };
    }

    public override edit() {
        super.edit();
        this.allDataFieldComponents?.forEach(e => e.edit());
    }

    public override commit() {
        super.commit();
        this.allDataFieldComponents?.forEach(e => e.commit());
    }

    public override cancel() {
        super.cancel();
        this.allDataFieldComponents?.forEach(e => e.cancel());
    }
}
